import React from 'react';

/**
 * Pages SDK runtime — what user-authored Page code reaches through the import
 * `@upsales/pages-sdk`. The bundler (WEB-6720) maps that import path to
 * `window.__pageRuntime.PagesSDK`, which this module populates.
 *
 * Three surfaces:
 *
 *   1. `tools.*` — every tool tagged `availableTo: 'page'` in the agent tool
 *      registry. A property access (`tools.readCRM(args)`) is intercepted by
 *      a Proxy that marshals the call across to the parent via postMessage.
 *      The parent (WEB-6721) validates the tool against the allowlist, runs
 *      it as the logged-in user, posts the result back.
 *
 *   2. React hooks — `useUser`, `useAccount`, `useNavigation`. Read from a
 *      React Context the iframe shell wraps around the Page component. The
 *      shell updates the context whenever a new `init` postMessage arrives.
 *
 *   3. Types — declared dynamically by the API (`GET /api/v2/pages/sdk-types.d.ts`)
 *      from the same registry. Not part of the runtime; consumed at authoring
 *      time by the chat.
 *
 * The SDK does no validation, no auth, no rate limiting. It's a thin
 * postMessage bridge — every security decision lives in the parent.
 */

export interface PageInit {
	userId: number | null;
	/** Tenant id — the `:customerId` segment of in-app hash routes. */
	customerId: number | null;
	clientId: number | null;
	orderId: number | null;
	language: string | null;
	currentRoute: string | null;
}

export const DEFAULT_RPC_TIMEOUT_MS = 30_000;

export class SDKError extends Error {
	code: 'timeout' | 'rejected' | 'malformed' | 'unknown';
	tool?: string;
	constructor(message: string, opts: { code: SDKError['code']; tool?: string } = { code: 'unknown' }) {
		super(message);
		this.name = 'SDKError';
		this.code = opts.code;
		this.tool = opts.tool;
	}
}

type Pending = {
	resolve: (value: unknown) => void;
	reject: (err: SDKError) => void;
	timer: ReturnType<typeof setTimeout>;
	tool: string;
};

interface RpcResponseMessage {
	type: 'rpc-response';
	id: number;
	data: unknown;
}
interface RpcErrorMessage {
	type: 'rpc-error';
	id: number;
	error: string;
}

const DEFAULT_INIT: PageInit = {
	userId: null,
	customerId: null,
	clientId: null,
	orderId: null,
	language: null,
	currentRoute: null
};

export const PageInitContext = React.createContext<PageInit>(DEFAULT_INIT);

/**
 * CRM record editors a Page can ask the host app to open. These map to the
 * standard Upsales modals/drawers on the parent side (see `sandboxBridge`).
 * The `PageEditorEntity` type and the parent's runtime `isOpenEditorRequest`
 * guard are both derived from the `PAGE_EDITOR_ENTITIES` array below, so they
 * can't drift. Account/contact are deliberately excluded (they open via
 * `useNavigation().open(...)`, so they never hit the popup-prone new-tab path).
 */
export const PAGE_EDITOR_ENTITIES = [
	'order',
	'agreement',
	'activity',
	'phonecall',
	'appointment',
	'todo',
	'ticket'
] as const;
export type PageEditorEntity = (typeof PAGE_EDITOR_ENTITIES)[number];

export interface PageNavigation {
	route: string | null;
	/**
	 * Navigate the host Upsales app to an in-app hash route in the SAME tab.
	 * Build the route with the tenant id from `useUser().customerId`, e.g.
	 * ``open(`#/${customerId}/accounts/123709426/`)`` to open an account card.
	 * Only same-origin `#/…` routes are honoured; anything else is ignored (no
	 * external links). Read the hook values during render and call `open` from a
	 * click handler.
	 */
	open: (url: string) => void;
	/**
	 * Open a standard CRM record editor (modal/drawer) in the host app for an
	 * EXISTING record — `openEditor('order', 456)` opens the order editor.
	 * Call from a real click handler.
	 */
	openEditor: (entity: PageEditorEntity, id: number) => void;
}

export interface PagesSDK {
	tools: Record<string, (args?: unknown) => Promise<unknown>>;
	useUser: () => PageInit;
	useAccount: () => { id: number } | null;
	useOrder: () => { id: number } | null;
	useNavigation: () => PageNavigation;
}

export interface CreateSdkOptions {
	postMessageTarget?: { postMessage: (data: unknown, targetOrigin: string) => void };
	timeoutMs?: number;
}

/**
 * Build the runtime SDK. Factored out from the iframe-shell bootstrap so
 * the marshaler can be unit-tested with a mock `postMessageTarget`.
 */
export function createPagesSDK(opts: CreateSdkOptions = {}): PagesSDK {
	const target = opts.postMessageTarget ?? window.parent;
	const timeoutMs = opts.timeoutMs ?? DEFAULT_RPC_TIMEOUT_MS;
	const pending = new Map<number, Pending>();
	let nextId = 0;

	const handleMessage = (event: MessageEvent) => {
		const data = event.data as RpcResponseMessage | RpcErrorMessage | undefined;
		if (!data || typeof data !== 'object') return;
		if (data.type === 'rpc-response') {
			const p = pending.get(data.id);
			if (!p) return;
			pending.delete(data.id);
			clearTimeout(p.timer);
			p.resolve(data.data);
		} else if (data.type === 'rpc-error') {
			const p = pending.get(data.id);
			if (!p) return;
			pending.delete(data.id);
			clearTimeout(p.timer);
			p.reject(new SDKError(data.error || 'RPC rejected', { code: 'rejected', tool: p.tool }));
		}
	};
	window.addEventListener('message', handleMessage);

	const invoke = (toolName: string, args: unknown): Promise<unknown> =>
		new Promise((resolve, reject) => {
			const id = ++nextId;
			const timer = setTimeout(() => {
				if (pending.has(id)) {
					pending.delete(id);
					reject(
						new SDKError(`Tool call timed out after ${timeoutMs}ms`, { code: 'timeout', tool: toolName })
					);
				}
			}, timeoutMs);
			pending.set(id, { resolve, reject, timer, tool: toolName });
			try {
				target.postMessage({ type: 'rpc', id, tool: toolName, args }, '*');
			} catch (err) {
				pending.delete(id);
				clearTimeout(timer);
				reject(
					new SDKError((err as Error)?.message ?? 'postMessage failed', { code: 'malformed', tool: toolName })
				);
			}
		});

	const tools = new Proxy({} as Record<string, (args?: unknown) => Promise<unknown>>, {
		get(_, toolName) {
			if (typeof toolName !== 'string') return undefined;
			return (args: unknown) => invoke(toolName, args);
		}
	});

	const useUser = (): PageInit => React.useContext(PageInitContext);

	// `useAccount`/`useOrder`/`useNavigation` must return a REFERENTIALLY STABLE
	// value across renders (WEB-7787). The obvious `return { id: ctx.clientId }`
	// mints a fresh object every render, so page code that writes
	// `const account = useAccount(); useEffect(fetch, [account])` re-fires the
	// effect on every render — the fetch setStates, which re-renders, which makes
	// a new `account`, which re-fires the fetch: a runaway readCRM loop. Memoizing
	// on the underlying primitive keeps the identity stable until the value
	// actually changes, so those dependency arrays behave.
	const useAccount = (): { id: number } | null => {
		const { clientId } = React.useContext(PageInitContext);
		return React.useMemo(() => (clientId !== null ? { id: clientId } : null), [clientId]);
	};

	const useOrder = (): { id: number } | null => {
		const { orderId } = React.useContext(PageInitContext);
		return React.useMemo(() => (orderId !== null ? { id: orderId } : null), [orderId]);
	};

	// Navigation is a fire-and-forget message to the parent (the trusted, un-
	// sandboxed host window), NOT an RPC — it never touches the server. The
	// host owns every security decision: it validates the target and does the
	// actual navigation / modal open. `open` and `openEditor` are closure-stable
	// so `useNavigation` can keep memoizing on `currentRoute` alone.
	const postNavigate = (msg: object): void => {
		try {
			target.postMessage(msg, '*');
		} catch {
			// parent unreachable from inside the sandbox — nothing we can do
		}
	};
	const open = (url: string): void => postNavigate({ type: 'page.navigate', url });
	const openEditor = (entity: string, id: number): void => postNavigate({ type: 'page.openEditor', entity, id });

	const useNavigation = (): PageNavigation => {
		const { currentRoute } = React.useContext(PageInitContext);
		return React.useMemo(() => ({ route: currentRoute, open, openEditor }), [currentRoute]);
	};

	return { tools, useUser, useAccount, useOrder, useNavigation };
}
